import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import FileFormatter from 'components/FileFormatter';
import TagColorFormatter from 'components/TagColorFormatter';
import { TagFormatter } from 'components/TagFormatter';
import JobContainer from 'components/styled/JobContainer';
import Button from 'components/Button';
import Notification from 'components/Notification';
import { GridTable } from 'components/GridTable';
import {
  ROLES,
  JOB_STATUS,
  colors,
  JOB_STAGE,
  PAGE_PATHS,
  FILE_TYPES,
  PRICE_TYPES,
} from '../../constants';
import { useTranslation } from 'react-i18next';
import { useReduxActions } from 'hooks/useReduxActions';
import {
  jobSelector,
  userSelector,
  searchReportValidationErrorsSelector,
  categoriesSelector,
} from '../../store/selectors';
import { useSelector } from 'react-redux';
import { BsThreeDots, BsDownload, BsPlus } from 'react-icons/bs';
import DropDown from 'components/DropDown';
import {
  ActionBar,
  ActionBarButton,
  ActionBarButtons,
} from 'components/styled/ActionBar';
import Text from 'components/styled/Text';
import { BiTrashAlt, BiCopy } from 'react-icons/bi';
import EmptyCell from 'components/EmptyCell';
import { getColumnsWidth } from 'utils/helpers';

function SearchReport() {
  const params = useParams();
  const { t } = useTranslation();
  const ref = useRef(null);
  const actions = useReduxActions();
  const user = useSelector(userSelector);
  const job = useSelector(jobSelector);
  const searchReportValidationErrors = useSelector(
    searchReportValidationErrorsSelector,
  );
  const categories = useSelector(categoriesSelector);
  const navigate = useNavigate();

  const [isDropDownShow, setIsDropDownShow] = useState(false);
  const [isNotificationShow, setIsNotificationShow] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentOptionsDropDownIndex, setCurrentOptionsDropDownIndex] =
    useState({});

  const columnsWidth = getColumnsWidth();
  const columnDefs = useMemo(
    () => [
      {
        field: 'selected',
        pinned: 'left',
        width: columnsWidth.selected,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        maxWidth: 50,
      },
      {
        headerName: t('search.report.header.id'),
        field: 'index',
        width: columnsWidth.index,
        pinned: 'left',
        maxWidth: 100,
      },
      {
        headerName: t('search.report.header.code_position'),
        field: 'code_position',
        width: columnsWidth.code_position,
        maxWidth: 200,
        pinned: 'left',
      },
      {
        headerName: t('search.report.header.product.name'),
        field: 'product_name',
        width: columnsWidth.product_name,
        pinned: 'left',
        minWidth: 200,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.category'),
        field: 'category',
        width: columnsWidth.category,
        minWidth: 150,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              show={
                props.rowIndex ===
                  currentOptionsDropDownIndex[props.colDef.field] && isEditable
              }
              right={-60}
              items={categories.map(item => ({
                text: item.name,
                onClick: handleSelectOption(item, props),
              }))}>
              {props?.value?.name ? (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {props?.value?.name}
                </Text>
              ) : (
                <EmptyCell
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}
                />
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('search.report.header.subcategory'),
        field: 'subcategory',
        width: columnsWidth.subcategory,
        minWidth: 150,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          const category = categories.find(
            c => c.name === props.data.category?.name,
          );
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              show={
                props.rowIndex ===
                  currentOptionsDropDownIndex[props.colDef.field] && isEditable
              }
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              items={
                category?.subcategory.map(item => ({
                  text: item.name,
                  onClick: handleSelectOption(item.name, props),
                })) || []
              }>
              {props?.value ? (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {props?.value}
                </Text>
              ) : (
                <EmptyCell
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}
                />
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('search.report.header.link'),
        hide: user.role === ROLES.CLIENT,
        field: 'link',
        width: columnsWidth.link,
        type: 'link',
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.photo.video'),
        field: 'photo_video',
        width: columnsWidth.photo_video,
        minWidth: 200,
        cellRenderer: props => (
          <FileFormatter
            editable={isEditable}
            data={props.data.photo_video}
            type={FILE_TYPES.IMAGE_AND_VIDEO}
            rowId={props.data.row_id}
            recall={reFetch}
            model="job"
          />
        ),
      },
      {
        headerName: t('search.report.header.color'),
        field: 'color',
        width: columnsWidth.color,
        minWidth: 250,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => (
          <TagColorFormatter
            rowIndex={props.rowIndex}
            data={props.data}
            editable={isEditable}
            onChangeRow={handleUpdateJob}
          />
        ),
      },
      {
        headerName: t('search.report.header.product.size'),
        field: 'product_size',
        width: columnsWidth.product_size,
        editable: isEditable,
        type: 'tags',
        cellRenderer: props => (
          <TagFormatter
            editable={isEditable}
            data={props}
            onDeleteTag={handleUpdateJob}
            field="product_size"
          />
        ),
      },
      {
        headerName: t('search.report.header.product.weight'),
        field: 'product_weight',
        width: columnsWidth.product_weight,
        type: 'weight',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.description'),
        field: 'description',
        width: columnsWidth.description,
        minWidth: 300,
        editable: isEditable,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
      },
      {
        headerName: t('search.report.header.EXW'),
        hide: user.role === ROLES.CLIENT,
        field: 'EXW',
        width: columnsWidth.EXW,
        type: 'chinaCurrency',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.MOQ'),
        field: 'MOQ',
        width: columnsWidth.MOQ,
        type: 'number',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.MOQ_FF'),
        hide: user.role === ROLES.CLIENT,
        field: 'MOQ_FF',
        width: columnsWidth.MOQ_FF,
        type: 'number',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.price'),
        field: 'price',
        width: columnsWidth.price,
        type: 'USDTCurrency',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.quantity'),
        field: 'quantity',
        width: columnsWidth.quantity,
        minWidth: 120,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.10pcs'),
        field: '10pcs',
        width: columnsWidth['10pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.50pcs'),
        field: '50pcs',
        width: columnsWidth['50pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.100pcs'),
        field: '100pcs',
        width: columnsWidth['100pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.500pcs'),
        field: '500pcs',
        width: columnsWidth['500pcs'],
        type: 'USDTCurrency',
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.box.size'),
        hide: user.role === ROLES.CLIENT,
        field: 'box_size',
        width: columnsWidth.box_size,
        editable: isEditable,
        type: ['tags', 'box'],
        cellRenderer: props => (
          <TagFormatter
            editable={isEditable}
            data={props}
            onDeleteTag={handleUpdateJob}
            field="box_size"
          />
        ),
      },
      {
        headerName: t('search.report.header.quantity.per.box'),
        hide: user.role === ROLES.CLIENT,
        field: 'quantity_per_box',
        width: columnsWidth.quantity_per_box,
        type: 'box',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('search.report.header.box.weight'),
        hide: user.role === ROLES.CLIENT,
        field: 'box_weight',
        width: columnsWidth.box_weight,
        type: ['weight', 'box'],
        minWidth: 150,
        editable: isEditable,
      },
    ],
    [isEditable, user.role, currentOptionsDropDownIndex], // eslint-disable-line
  );

  const getRowData = () => {
    const rowData = [];
    ref.current.api.forEachNode(node => {
      if (
        node.data['10pcs'] ||
        node.data['50pcs'] ||
        node.data['100pcs'] ||
        node.data['500pcs'] ||
        node.data.EXW
      ) {
        node.data.price_type = PRICE_TYPES.FIX;
      }
      if (node.data.quantity || node.data.price) {
        node.data.price_type = PRICE_TYPES.FLEX;
      }
      rowData.push(node.data);
    });
    return rowData;
  };

  const addRows = useCallback(quantity => {
    const newItems = [...Array(quantity)].map(() => ({ row_id: uuidv4() }));
    actions.addJobSearchReportRows(params.ID, newItems);
  }, []); // eslint-disable-line

  const handleOpenOptionsDropDown = (index, field) => () => {
    if (currentOptionsDropDownIndex[field] === index) {
      setCurrentOptionsDropDownIndex({ [field]: null });
    } else {
      setCurrentOptionsDropDownIndex({ [field]: index });
    }
  };

  const handleSelectOption = (item, props) => () => {
    const preData = getRowData();
    const newData = preData.map(row => {
      return row.row_id === props.data.row_id
        ? { ...row, [props.colDef.field]: item }
        : row;
    });
    actions.updateJobSearchReport(params.ID, newData);
    setCurrentOptionsDropDownIndex({ [props.colDef.field]: null });
  };

  const handleChangeJob = useCallback((_, cell) => {
    const data = getRowData();
    const { field } = cell.colDef;
    if (field === 'EXW') {
      cell.data['10pcs'] = (cell.data.EXW * 1.28) / 6.66;
      cell.data['50pcs'] = (cell.data.EXW * 1.2) / 6.66;
      cell.data['100pcs'] = (cell.data.EXW * 1.18) / 6.66;
      cell.data['500pcs'] = (cell.data.EXW * 1.13) / 6.66;
      cell.data.price = null;
      cell.data.quantity = null;
    }
    if (
      field === '10pcs' ||
      field === '50pcs' ||
      field === '100pcs' ||
      field === '500pcs'
    ) {
      cell.data.price = null;
      cell.data.quantity = null;
    }
    if (field === 'quantity' || field === 'price') {
      cell.data['10pcs'] = null;
      cell.data['50pcs'] = null;
      cell.data['100pcs'] = null;
      cell.data['500pcs'] = null;
      cell.data.EXW = null;
    }
    actions.changeJobSearchReport(params.ID, data);
  }, []); // eslint-disable-line

  const handleUpdateJob = cell => {
    const preData = getRowData();
    const newData = preData.map(item =>
      item.row_id === cell.row_id ? cell : item,
    );
    actions.updateJobSearchReport(params.ID, newData);
  };

  const reFetch = () => {
    actions.getJobById(params.ID);
  };

  const handleAddToProduct = async () => {
    try {
      const ids = selectedRows.map(row => row.row_id);
      actions.addSearchReportRowToProduct(params.ID, ids);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSendToClientButton = async () => {
    try {
      const filteredChosenProducts = job.search_report_payload.filter(
        item => !item.code_position,
      );
      const decoratedChosenProducts = filteredChosenProducts.map(item => ({
        ...item,
        isDraft: false,
      }));
      const response = await actions.createProduct(decoratedChosenProducts);
      if (response) {
        setIsNotificationShow(true);
        actions.sendToClient(params.ID);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDotsButton = () => {
    setIsDropDownShow(!isDropDownShow);
  };

  const handleCloseDropDown = () => {
    setIsDropDownShow(false);
  };

  const handleSelectItem = useCallback(() => {
    setSelectedRows(ref.current.api.getSelectedRows());
  }, []);

  const handleDeleteButton = () => {
    const ids = selectedRows.map(row => row.row_id);
    actions.deleteJobSearchReportRows(params.ID, ids);
  };

  const handleDuplicateButton = () => {
    const copiedRows = [...selectedRows];
    const newItems = copiedRows.map(row => ({
      ...row,
      row_id: uuidv4(),
    }));
    actions.duplicateJobSearchReportRows(params.ID, newItems);
  };

  const closeNotification = () => {
    setIsNotificationShow(false);
  };

  const handleDownload = () => {};

  const handleAddToQuote = async () => {
    try {
      const ids = selectedRows.map(row => row.row_id);
      const res = await actions.createQuote(params.ID, ids);
      if (res?.stage === JOB_STAGE.QUOTE) {
        navigate(
          `/${PAGE_PATHS.CREATE_JOB.ROOT}/${PAGE_PATHS.CREATE_JOB.QUOTE}/${params.ID}`,
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    setIsEditable(!(user.role === ROLES.CLIENT || user.role === ROLES.HELPER));
  }, [user]); // eslint-disable-line

  useEffect(() => {
    actions.getJobById(params.ID);
    actions.getAllCategories();
  }, []); // eslint-disable-line

  return (
    <>
      {user.role !== ROLES.CLIENT &&
        (!!selectedRows.length ? (
          <ActionBar>
            <ActionBarButtons>
              <ActionBarButton onClick={handleDuplicateButton}>
                <BiCopy fill={colors.grey40} />
                <Text variant="body" color={colors.navy}>
                  {t('search.report.action.bar.duplicate.button')}
                </Text>
              </ActionBarButton>
              <ActionBarButton onClick={handleAddToQuote}>
                <BsPlus fill={colors.grey40} />
                <Text variant="body" color={colors.navy}>
                  {t('search.report.action.bar.add.to.quote.button')}
                </Text>
              </ActionBarButton>
              <ActionBarButton onClick={handleAddToProduct}>
                <BsPlus fill={colors.grey40} />
                <Text variant="body" color={colors.navy}>
                  {t('search.report.action.bar.add.to.product.button')}
                </Text>
              </ActionBarButton>
            </ActionBarButtons>
            <ActionBarButtons>
              <ActionBarButton onClick={handleDeleteButton}>
                <BiTrashAlt fill={colors.red} />
                <Text variant="body" color={colors.red}>
                  {t('search.report.action.bar.delete.button')}
                </Text>
              </ActionBarButton>
            </ActionBarButtons>
          </ActionBar>
        ) : (
          <JobContainer>
            {job?.status[user?.role] === JOB_STATUS.IN_PROGRESS ? (
              <Button
                size="small"
                variant="primary"
                onClick={handleSendToClientButton}>
                {t('search.report.send.to.client.button')}
              </Button>
            ) : (
              <Button size="small" variant="secondary" onClick={handleDownload}>
                <BsDownload style={{ marginRight: '8px' }} />
                {t('search.report.download.button')}
              </Button>
            )}
            <DropDown
              onOutsideClick={handleCloseDropDown}
              show={isDropDownShow}
              items={[]}>
              <Button onClick={handleDotsButton} size="small" variant="icon">
                <BsThreeDots />
              </Button>
            </DropDown>
          </JobContainer>
        ))}
      {user.role === ROLES.CLIENT &&
        (!!selectedRows.length ? (
          <ActionBar>
            <ActionBarButtons>
              <ActionBarButton onClick={handleAddToQuote}>
                <BsPlus fill={colors.grey40} />
                <Text variant="body" color={colors.navy}>
                  {t('search.report.action.bar.add.to.quote.button')}
                </Text>
              </ActionBarButton>
            </ActionBarButtons>
          </ActionBar>
        ) : (
          <JobContainer>
            <Button size="small" variant="secondary" onClick={handleDownload}>
              <BsDownload style={{ marginRight: '8px' }} />
              {t('search.report.download.button')}
            </Button>
            <DropDown
              onOutsideClick={handleCloseDropDown}
              show={isDropDownShow}
              items={[]}>
              <Button onClick={handleDotsButton} size="small" variant="icon">
                <BsThreeDots />
              </Button>
            </DropDown>
          </JobContainer>
        ))}
      <Notification
        show={isNotificationShow}
        text={t('search.report.notification.text')}
        title={t('search.report.notification.title')}
        buttonName={t('search.report.notification.button')}
        onClick={closeNotification}
      />
      {!(
        user?.role === ROLES.CLIENT &&
        job?.status[user.role] === JOB_STATUS.IN_PROGRESS
      ) &&
        job && (
          <GridTable
            rows={job.search_report_payload}
            errors={searchReportValidationErrors}
            columnDefs={columnDefs}
            onAddItems={addRows}
            onUpdateItem={handleChangeJob}
            onSelectItem={handleSelectItem}
            ref={ref}
            rowSelection="multiple"
            defaultRowsNumber={5}
            isEditable={isEditable}
          />
        )}
      {user?.role === ROLES.CLIENT &&
        job?.status[user.role] === JOB_STATUS.IN_PROGRESS && (
          <Text variant="body">{t('search.report.work.in.progress.text')}</Text>
        )}
    </>
  );
}

export default SearchReport;
